<template>
  <div class="responsibility-line-menu">
    <!-- Production mode: Show DropDownMenu with offsets -->
    <DropDownMenu
      v-if="!useInternalDialogs"
      :dropdownItems="visibleMenuItems"
      :isOpen="isMenuOpen"
      @onClick="handleMenuClick"
      :menuWidth="true"
      :translate-x-offset="12"
      :translate-y-offset="30"
    >
      <template #activator>
        <div class="icon-container">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="icon-svg"
          >
            <path
              d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </template>
    </DropDownMenu>

    <!-- Storybook mode: Show DropDownMenu without offsets -->
    <DropDownMenu
      v-if="useInternalDialogs"
      :dropdownItems="visibleMenuItems"
      :isOpen="isMenuOpen"
      @onClick="handleMenuClick"
      :menuWidth="true"
    >
      <template #activator>
        <div class="icon-container">
        </div>
      </template>
    </DropDownMenu>

    <!-- Dialogs for Storybook (only shown when useInternalDialogs is true) -->
    <template v-if="useInternalDialogs">
      <ActivateResponsibility
        v-model="showDialog.activate"
        :responsibility="props.responsibilityName"
        @onSave="() => actionHandler('activate')"
      />
      <DeleteResponsibility
        v-model="showDialog.delete"
        :responsibility="props.responsibilityName"
        @onDelete="() => actionHandler('delete')"
      />
      <RetireResponsibility
        :retireResponsibility="true"
        v-model="showDialog.retire"
        :responsibility="props.responsibilityName"
        @onSave="() => actionHandler('retire')"
      />
      <ReActivateResponsibility
        v-model="showDialog.reactivate"
        :responsibility="props.responsibilityName"
        @onSave="() => actionHandler('reactivate')"
      />
      <RenameResponsibilityDialog
        v-if="showDialog.rename"
        v-model="showDialog.rename"
        :responsibility="renameResponsibilityModel"
        @saved="(updated) => actionHandler('rename', updated.name)"
      />
      <ArchivePopUp
        v-if="showDialog.archive || showDialog.unarchive"
        :action="showDialog.archive ? 'archive' : 'unarchive'"
        :type="'responsibility'"
        :show="showDialog.archive || showDialog.unarchive"
        :curretObj="{ name: props.responsibilityName }"
        @onClose="closeArchiveDialog"
        @onArchive="() => actionHandler(showDialog.archive ? 'archive' : 'unarchive')"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, reactive, watch } from "vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import ActivateResponsibility from "@components/RolesCatalog/ActivateResponsibility.vue";
import DeleteResponsibility from "@components/RolesCatalog/DeleteResponsibility.vue";
import RetireResponsibility from "@components/RolesCatalog/RetireResponsibility.vue";
import ReActivateResponsibility from "@components/RolesCatalog/ReActivateResponsibility.vue";
import RenameResponsibilityDialog from "@components/RolesCatalog/RenameResponsibilityDialog/RenameResponsibilityDialog.vue";
import ArchivePopUp from "@components/common/ArchivePopUp.vue";

const props = defineProps({
  status: {
    type: String,
    default: "Draft",
    validator: (s) => ["Draft", "Active", "Retired", "Archived"].includes(s),
  },
  responsibilityName: {
    type: String,
    default: "",
  },
  menuOpen: {
    type: Boolean,
    default: false,
  },
  // When true, shows internal dialogs (for Storybook)
  // When false (default), emits action to parent
  useInternalDialogs: {
    type: Boolean,
    default: false,
  },
  // Header menus (Responsibility detail page) keep Attach to Role; row menus do not.
  isHeaderMenu: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["action"]);

// In Storybook mode (useInternalDialogs = true), menu is always open
const isMenuOpen = ref(props.useInternalDialogs ? true : props.menuOpen);

const renameResponsibilityModel = computed(() => ({
  id: null,
  name: props.responsibilityName,
  context: "",
  status: props.status,
  archived: props.status === "Archived",
}));

const showDialog = reactive({
  activate: false,
  delete: false,
  retire: false,
  reactivate: false,
  rename: false,
  archive: false,
  unarchive: false,
});

watch(
  () => props.menuOpen,
  (newVal) => {
    // In Storybook mode, always keep menu open
    isMenuOpen.value = props.useInternalDialogs ? true : newVal;
  }
);

const visibleMenuItems = computed(() =>
  [
    props.status === "Draft" && {
      name: "Activate",
      value: "activate",
      icon: "icons/dropdown/activate.svg",
      destructive: false,
    },
    props.status === "Draft" && {
      name: "Delete",
      value: "delete",
      icon: "icons/dropdown/trash.svg",
      destructive: true,
      negative: true,
    },
    props.status === "Active" && {
      name: "Retire",
      value: "retire",
      icon: "icons/dropdown/activate.svg",
      destructive: false,
    },
    props.status === "Retired" && {
      name: "Reactivate",
      value: "reactivate",
      icon: "icons/dropdown/activate.svg",
      destructive: false,
    },
    // Rename: Always
    {
      name: "Rename",
      value: "rename",
      icon: "icons/dropdown/rename.svg",
      destructive: false,
    },
    props.isHeaderMenu && {
      name: "Attach to Role",
      value: "attach_to_role",
      icon: "icons/dropdown/attach.svg",
      destructive: false,
    },

    props.status === "Retired" && {
      name: "Archive",
      value: "archive",
      icon: "icons/dropdown/archive-new.svg",
      destructive: false,
    },
    props.status === "Archived" && {
      name: "Unarchive",
      value: "unarchive",
      icon: "icons/dropdown/activate.svg",
      destructive: false,
    },
  ].filter(Boolean)
);

function handleMenuClick(item) {
  if (props.useInternalDialogs) {
    // For Storybook: show internal dialogs
    resetDialogs();
    switch (item.value) {
      case "activate":
        showDialog.activate = true;
        break;
      case "delete":
        showDialog.delete = true;
        break;
      case "retire":
        showDialog.retire = true;
        break;
      case "reactivate":
        showDialog.reactivate = true;
        break;
      case "rename":
        showDialog.rename = true;
        break;
      case "archive":
        showDialog.archive = true;
        break;
      case "unarchive":
        showDialog.unarchive = true;
        break;
      case "attach_to_role":
        // Attach to role always emits to parent
        emit("action", item.value);
        break;
    }
  } else {
    // For production: emit to parent, parent handles dialogs
    emit("action", item.value);
  }
}

function resetDialogs() {
  Object.keys(showDialog).forEach((k) => {
    showDialog[k] = false;
  });
}

function closeArchiveDialog() {
  showDialog.archive = false;
  showDialog.unarchive = false;
}

function actionHandler(type, data = null) {
  resetDialogs();
  isMenuOpen.value = false;
  // Only emit to parent in production mode (useInternalDialogs = false)
  // In Storybook mode, just close the dialog without emitting
  if (!props.useInternalDialogs) {
    emit("action", { type, data });
  }
}

defineExpose({ visibleMenuItems, handleMenuClick });
</script>

<style scoped>
.responsibility-line-menu {
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.icon-svg {
  display: block;
}
</style>
